import { createSlice } from "@reduxjs/toolkit";
import { v4 as createUuid } from "uuid";
import JsonParams from "meta/features/json-params";
import { loginUrl, patientDetailUrl, patientListUrl, } from "../../utils/constants";
import { patientDetailMenuId } from "../../utils/constants";
import { getMenuIdFromUrl, getVisIdFromInitialUrl, updateQueryStringParameter, } from "meta/utils/url";
import { DraggableTabsOrdering } from "./DraggableTabsOrdering";
const mainTabUUID = createUuid();
const initialTabs = {};
initialTabs[mainTabUUID] = {
    title: "",
    url: "",
    loaded: true,
};
const initialState = {
    serverInfo: { name: JsonParams.ServerInfo.name },
    installationInfo: {
        printerGroups: JsonParams.InstallationInfo.printerGroups,
        fdbLicenseStatus: JsonParams.FDBLicenseStatus
    },
    appSettings: JsonParams.AppSettings,
    headlessLoginResult: undefined,
    isLogged: false,
    activeTabKey: mainTabUUID,
    activeDockedTabKey: undefined,
    dockedTabKeys: [],
    tabs: initialTabs,
    progress: -1,
    isResizingActive: false,
    userPreferences: {},
    rightPanelExpanded: true,
    inactivityLogoutActive: false
};
const createInitialTabs = (staticData) => !staticData.HeadlessMode || !staticData.HeadlessModeInitialTab && !staticData.HeadlessModeInitialUrl && !staticData.HeadlessModeChartTab
    ? staticData.InitialTabs
    : loadHeadlessModeInitialTabs(staticData);
const loadHeadlessModeInitialTabs = (staticData) => {
    if (staticData.HeadlessModeInitialTab) {
        const initTabId = staticData.HeadlessModeInitialTab.toLowerCase();
        const url = updateQueryStringParameter(staticData.MenuItems[initTabId].Url, "menuId", initTabId);
        return loadHeadlessModeInitialTabsCore(staticData, url, staticData.MenuItems[initTabId].Title);
    }
    if (staticData.HeadlessModeInitialUrl) {
        const menuId = getMenuIdFromUrl(staticData.HeadlessModeInitialUrl);
        const title = menuId ? staticData.MenuItems[menuId]?.Title : "";
        const url = `${patientDetailUrl}&initialUrl=${encodeURIComponent(staticData.HeadlessModeInitialUrl)}&initialTitle=${title}`;
        return loadHeadlessModeInitialTabsCore(staticData, url, "Patient Chart");
    }
    const url = patientDetailUrl +
        (staticData.HeadlessModeChartTab
            ? "&initialTab=" + staticData.HeadlessModeChartTab
            : "");
    return loadHeadlessModeInitialTabsCore(staticData, url, "Patient Chart");
};
const loadHeadlessModeInitialTabsCore = (staticData, url, title) => {
    return [
        ...(
        // in headless mode, only load a singular horizontal tab
        staticData.HeadlessModeEnabled
            ? []
            : staticData.InitialTabs),
        {
            URL: url,
            Title: title,
            Locked: false,
            Docked: false,
            VisID: staticData.HeadlessModePatient,
            Order: 0,
        },
    ];
};
const openInitialTabs = (state) => {
    const { appSettings: { StaticData }, } = state;
    const tabsToOpen = createInitialTabs(StaticData);
    const initTabs = {};
    tabsToOpen.forEach((tab, index) => {
        const menuId = getMenuIdFromUrl(tab.URL);
        const tabKey = createUuid();
        initTabs[tabKey] = {
            title: tab.Title || "Page",
            url: tab.URL,
            frameSession: createUuid(),
            menuId: menuId,
            loaded: false,
            progress: -1,
            locked: tab.Locked || false,
            dockable: (StaticData.DockableMenuItems || []).includes(menuId),
            initialized: false,
            order: index,
            VisID: tab.VisID,
        };
    });
    const keys = Object.keys(initTabs);
    const activeTabKey = keys[StaticData.HeadlessMode ? keys.length - 1 : 0];
    const dockableInitTabs = keys.filter((key) => initTabs[key].dockable);
    const defaultDocked = dockableInitTabs ? dockableInitTabs[0] : undefined;
    if (activeTabKey) {
        initTabs[activeTabKey].loaded = true;
    }
    if (defaultDocked) {
        initTabs[defaultDocked].loaded = true;
    }
    state.tabs = initTabs;
    state.activeTabKey = activeTabKey;
    state.activeDockedTabKey = defaultDocked;
    state.dockedTabKeys = dockableInitTabs ? dockableInitTabs : [];
};
const applyLogin = (state, data) => {
    state.isLogged = true;
    state.appSettings.User = data.User;
    const menuItems = {};
    data.StaticData.MenuItems.forEach((item) => (menuItems[item.ID.toLocaleLowerCase()] = item));
    const baseData = data.StaticData;
    state.appSettings.StaticData = baseData;
    state.appSettings.StaticData.MenuItems = menuItems;
    state.userPreferences =
        data.UserPreferences ||
            { TabsDockedWidth: {} };
    openInitialTabs(state);
};
const applyInit = (state, data) => {
    const tabKey = createUuid();
    state.isLogged = data.isLogged;
    state.activeTabKey = tabKey;
    state.tabs = {};
    const initialTab = {
        frameSession: createUuid(),
        loaded: true,
        progress: -1,
        order: DraggableTabsOrdering.initialOrder,
    };
    state.tabs[tabKey] = {
        ...(data.url
            ? {
                title: data.title || "Page",
                url: data.url,
                menuId: getMenuIdFromUrl(data.url),
            }
            : {
                // Title will not be visible anyway
                title: "Welcome",
                url: loginUrl,
            }),
        ...initialTab,
    };
};
if (JsonParams.LoginResult) {
    if (JsonParams.LoginResult.Success) {
        applyLogin(initialState, JsonParams.LoginResult.Data);
        initialState.headlessLoginResult = {
            success: true
        };
    }
    else {
        applyInit(initialState, { isLogged: false });
        initialState.headlessLoginResult = {
            success: false,
            failureReason: JsonParams.LoginResult.Reason
        };
    }
}
else if (JsonParams.MetaPortalInfo) {
    applyInit(initialState, { url: JsonParams.MetaPortalInfo.RedirectUrl, isLogged: true });
}
else {
    applyInit(initialState, { isLogged: false });
}
export const rootSlice = createSlice({
    name: "root",
    initialState,
    reducers: {
        appReset: (state) => {
            openInitialTabs(state);
        },
        appInit: (state, action) => {
            applyInit(state, action.payload);
        },
        userLogin: (state, action) => {
            //USER_LOGIN
            applyLogin(state, action.payload);
        },
        addContentTab: (state, action) => {
            const newTabKey = createUuid();
            const refreshToken = createUuid();
            const { docked = action.payload.docked || action.payload.forceDocked, forceDocked, frameSession, locked, menuId, title, url, width, order, } = action.payload;
            const newTab = {
                url,
                title,
                locked,
                menuId,
                frameSession,
                loaded: true,
                dockable: state.appSettings.StaticData &&
                    (state.appSettings.StaticData.DockableMenuItems || []).includes(menuId),
                progress: -1,
                forceDocked,
                width,
                refreshToken,
                VisID: getVisIdFromInitialUrl(url),
                order: DraggableTabsOrdering.createSpot(state, docked, order),
            };
            state.tabs[newTabKey] = newTab;
            if (docked) {
                state.dockedTabKeys = [...state.dockedTabKeys, newTabKey];
                state.activeDockedTabKey = newTabKey;
                state.rightPanelExpanded = true;
            }
            else {
                state.activeTabKey = newTabKey;
            }
        },
        patientSelected: (state, action) => {
            //PATIENT_SELECTED
            const tabKey = Object.keys(state.tabs).find((key) => state.tabs[key].frameSession === action.payload._frameSession);
            if (tabKey !== undefined) {
                const tab = state.tabs[tabKey];
                tab.VisID = action.payload.VisID;
                tab.PatientInfoUpdateJs = action.payload.PatientInfoUpdateJs;
                tab.PatientInfoExtended = action.payload.PatientInfoExtended;
                tab.PatientName = action.payload.PatientName;
                tab.VisitAccount = action.payload.VisitAccount;
                tab.PageTitle = action.payload.PageTitle;
            }
        },
        updateUserInfo: (state, action) => {
            state.appSettings.User = { ...state.appSettings.User, ...action.payload };
        },
        setContentLocked: (state, action) => {
            state.tabs[state.activeTabKey].locked = action.payload;
        },
        setDockedTabActive: (state, action) => {
            state.activeDockedTabKey = action.payload;
            state.rightPanelExpanded = true;
        },
        setDockedTab: (state, action) => {
            DraggableTabsOrdering.dock(state, action.payload);
            state.tabs[action.payload].loaded = true;
            state.activeDockedTabKey = action.payload;
            state.rightPanelExpanded = true;
            state.dockedTabKeys = [...state.dockedTabKeys, action.payload];
            state.activeTabKey =
                state.activeTabKey === action.payload
                    ? Object.keys(state.tabs).filter((key) => action.payload !== key)[0]
                    : state.activeTabKey;
        },
        setActiveTab: (state, action) => {
            state.activeTabKey = action.payload;
            state.tabs[action.payload].loaded = true;
        },
        openContentUrl: (state, action) => {
            const activeTab = state.tabs[state.activeTabKey];
            const newUrl = action.payload.url +
                `${action.payload.url.indexOf("?") !== -1 ? "&" : "?"}timeStamp=${new Date().getTime()}`;
            activeTab.title = action.payload.title;
            activeTab.menuId = action.payload.menuId;
            activeTab.modified = false;
            activeTab.dockable = (state.appSettings.StaticData.DockableMenuItems || []).includes(action.payload.menuId);
            activeTab.children = undefined;
            if (newUrl !== activeTab.url) {
                activeTab.VisID = undefined;
                activeTab.PageTitle = undefined;
                activeTab.PatientInfoUpdateJs = undefined;
                activeTab.PatientName = undefined;
                activeTab.VisitAccount = undefined;
            }
            activeTab.url = newUrl;
        },
        undockTab: (state, action) => {
            DraggableTabsOrdering.undock(state, action.payload);
            const newDockedKeys = state.dockedTabKeys.filter((key) => key !== action.payload);
            state.activeDockedTabKey =
                newDockedKeys.length > 0 ? newDockedKeys[0] : undefined;
            state.dockedTabKeys = newDockedKeys;
            state.activeTabKey = action.payload;
        },
        // TODO: Rework
        closeContentTab: (state, action) => {
            let activeTabKey = state.activeTabKey;
            if (action.payload === activeTabKey) {
                const menuId = state.tabs[activeTabKey].menuId;
                //PatientSearchAndList do not have uniqueId. Due to we must find it by url.
                const patientSearchTabKey = Object.keys(state.tabs).find((key) => state.tabs[key].url === patientListUrl);
                if (patientSearchTabKey && menuId === patientDetailMenuId) {
                    activeTabKey = patientSearchTabKey;
                }
                else {
                    const tabsKeysSequence = Object.keys(state.tabs).filter((key) => !state.dockedTabKeys.includes(key));
                    const currentIndex = tabsKeysSequence.indexOf(activeTabKey);
                    const next = tabsKeysSequence[currentIndex - 1];
                    activeTabKey = next;
                }
            }
            DraggableTabsOrdering.remove(state, action.payload);
            delete state.tabs[action.payload];
            // make sure the new active or docked tab is loaded
            if (activeTabKey) {
                state.tabs[activeTabKey].loaded = true;
            }
            const newDockedKeys = state.dockedTabKeys.filter((key) => key !== action.payload);
            state.activeTabKey = activeTabKey;
            state.activeDockedTabKey =
                state.activeDockedTabKey === action.payload
                    ? newDockedKeys.length > 0
                        ? newDockedKeys[0]
                        : undefined
                    : state.activeDockedTabKey;
            state.dockedTabKeys = newDockedKeys;
            state.progress = -1;
        },
        setContentTitle: (state, action) => {
            state.tabs[action.payload.key].title = action.payload.title;
        },
        clearPatientFromTab: (state) => {
            state.tabs[state.activeTabKey].VisID = undefined;
            state.tabs[state.activeTabKey].PageTitle = undefined;
            state.tabs[state.activeTabKey].PatientInfoUpdateJs = undefined;
            state.tabs[state.activeTabKey].PatientInfoExtended = undefined;
            state.tabs[state.activeTabKey].PatientName = undefined;
            state.tabs[state.activeTabKey].VisitAccount = undefined;
        },
        tabInitialized: (state, action) => {
            state.tabs[action.payload].initialized = true;
        },
        contentLoad: (state, action) => {
            const tab = state.tabs[action.payload.key];
            tab.menuId = action.payload.menuId;
            tab.title = action.payload.title;
            tab.modified = false;
            tab.dockable = (state.appSettings.StaticData.DockableMenuItems || []).includes(action.payload.menuId);
        },
        progressDone: (state, action) => {
            if (state.tabs[action.payload]) {
                state.tabs[action.payload].progress = 100;
            }
        },
        progressStart: (state, action) => {
            if (state.tabs[action.payload])
                state.tabs[action.payload].progress = 1;
        },
        setContentModified: (state, action) => {
            if (state.tabs[action.payload.key])
                state.tabs[action.payload.key].modified = action.payload.modified;
        },
        setCurrentContentUrl: (state, action) => {
            const tab = state.tabs[state.activeTabKey];
            const children = tab.children;
            const url = `${action.payload.url}${action.payload.url.indexOf("?") !== -1 ? "&" : "?"}timeStamp=${new Date().getTime()}`;
            if (children) {
                children[tab.activeTabKey].url = url;
            }
            else {
                const menuId = getMenuIdFromUrl(url);
                tab.url = url;
                tab.title = action.payload.title || tab.title;
                tab.menuId = menuId;
                tab.dockable = (state.appSettings.StaticData.DockableMenuItems || []).includes(menuId);
                tab.modified = false;
            }
        },
        reloadTopMenu: (state, aciton) => {
            state.appSettings.StaticData.TopMenus = aciton.payload;
        },
        reloadContentTab: (state, action) => {
            const tabKeyToRefresh = action?.payload.key || state.activeTabKey;
            const tabToRefresh = state.tabs[tabKeyToRefresh];
            if (patientDetailMenuId === tabToRefresh.menuId) {
                tabToRefresh.url = updateQueryStringParameter(tabToRefresh.url, "loadFromStorage", action?.payload.disableCache ? "false" : "true");
            }
            tabToRefresh.refreshToken = createUuid();
            tabToRefresh.modified = false;
        },
        reorderTabs: DraggableTabsOrdering.reorder,
        setResizingActive: (state, action) => {
            state.isResizingActive = action.payload;
        },
        setRightPaneWidth: (state, action) => {
            if (state.activeDockedTabKey && state.tabs[state.activeDockedTabKey]) {
                const activeDockedTabMenuId = state.tabs[state.activeDockedTabKey].menuId;
                if (activeDockedTabMenuId)
                    state.userPreferences.TabsDockedWidth[activeDockedTabMenuId] =
                        action.payload;
            }
        },
        toggleRightPanel: (state) => {
            state.rightPanelExpanded = !state.rightPanelExpanded;
        },
        unblockFdbLicense: (state) => {
            state.installationInfo.fdbLicenseStatus.fdbLicenseUnblocked = true;
        },
        setInactivityLogoutActive: (state) => { state.inactivityLogoutActive = true; },
        setReadyToAutoLogout: (state, action) => {
            state.tabs[action.payload].readyToAutoLogout = true;
        }
    },
});
// Action creators are generated for each case reducer function
export const { appInit, appReset, userLogin, updateUserInfo, setContentLocked, setDockedTabActive, setContentTitle, clearPatientFromTab, progressStart, progressDone, contentLoad, addContentTab, patientSelected, setActiveTab, closeContentTab, setDockedTab, openContentUrl, undockTab, setContentModified, setCurrentContentUrl, reloadTopMenu, tabInitialized, reloadContentTab, reorderTabs, setResizingActive, setRightPaneWidth, toggleRightPanel, unblockFdbLicense, setInactivityLogoutActive, setReadyToAutoLogout } = rootSlice.actions;
export default rootSlice.reducer;
